import { NoopScrollStrategy } from "@angular/cdk/overlay";
import { Component, Input, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { RangeFeature } from "src/app/model/catalog/catalog.model";
import { LensType } from "src/app/model/configuration/configuration";
import { GlobalStorageService } from "src/app/service-utils/global-storage.service";
import { i18nService } from "src/app/service-utils/i18n.service";
import { ValidatorService } from "src/app/service-utils/validator-service";
import { LensSide } from "src/app/utils/catalog.service";
import { NewOrderStoreService } from "../../new-order-store-service.service";
import { EccentricityDialogComponent } from "../eccentricity-dialog/eccentricity-dialog.component";

@Component({
    selector: 'app-central-radius',
    templateUrl: './central-radius.component.html',
    styleUrls: ['./central-radius.component.scss']
})
export class CentralRadiusComponent implements OnInit {
    @Input() side: LensSide;

    LensType = LensType;
    form: FormGroup;
    previousEccentricityDialogResult = [];
    numberRegEx = '^\d+(?:[,.]\d+)?$';

    radiusSteilFeature = new RangeFeature('RadiusSteil', true, 4.5, 15 , 0.01);

    constructor(
        public i18nService: i18nService,
        private formBuilder: FormBuilder,
        public dialog: MatDialog,
        private newOrderStoreService: NewOrderStoreService,
        private router: Router,
        private validatorService: ValidatorService,
        private globalStorageService: GlobalStorageService
    ) {}

    ngOnInit(): void {
        this.initForm();
    }

    public initForm() {
        this.form = this.formBuilder.group({
            radiusFlach: ["", [this.validatorService.numericValidator('RadiusFlach'), this.validatorService.requiredFieldValidator('RadiusFlach'), this.validatorService.rangeValidator(new RangeFeature('RadiusFlach', true, 4.5, 15, 0.01))]],
            radiusFlachDegree: ["", [this.validatorService.numericValidator('RadiusFlachDegree'), this.validatorService.requiredFieldValidator('RadiusFlachDegree'), this.validatorService.rangeValidator(new RangeFeature('RadiusFlachDegree', true, 0, 179, 1))]],
            radiusSteil: ["", [this.validatorService.numericValidator('RadiusSteil'), this.validatorService.requiredFieldValidator('RadiusSteil'), this.validatorService.rangeValidator(this.radiusSteilFeature)]],
            radiusSteilDegree: [],
            eccentricity: ["", [this.validatorService.numericValidator('Eccentricity'), this.validatorService.requiredFieldValidator('Eccentricity'), this.validatorService.rangeValidator(new RangeFeature('Eccentricity', true, -2, 2, 0.01))]],
            hhDia: ["", [this.validatorService.numericValidator('HHDia'), this.validatorService.requiredFieldValidator('HHDia'), this.validatorService.rangeValidator(new RangeFeature('HHDia', true, 9, 14, 0.1))]],
            kCStadium: [0, [this.validatorService.numericValidator('KCStadium'), this.validatorService.requiredFieldValidator('KCStadium'), this.validatorService.rangeValidator(new RangeFeature('KCStadium', true, 0, 3, 1))]],
            torizitatCentral: [],
            astigmat: [],
            lensType: [null, this.validatorService.requiredFieldValidator('LensType')]
        });
        this.initHandlerForm();
    }

    private initHandlerForm() {
        this.form.get('radiusFlachDegree').valueChanges.subscribe(() =>{
            this.recalculateRadiusSteilDegree();
        });

        this.form.get('radiusFlach').valueChanges.subscribe(() =>{
            this.reInitRadiusSteilValidators();
            this.recalculateTorizitalAstigmat();
        });

        this.form.get('radiusSteil').valueChanges.subscribe(() =>{
            this.recalculateTorizitalAstigmat();
        });
    }

    private reInitRadiusSteilValidators() {
        if(this.form.get('radiusFlach').value !== null) {
            this.form.get('radiusSteil').clearValidators();
            if(this.i18nService.replaceFromCommaToDot(this.form.get('radiusFlach').value) < 4.5 || this.i18nService.replaceFromCommaToDot(this.form.get('radiusFlach').value) > 15) {
                this.form.get('radiusSteil').setValidators([this.validatorService.requiredFieldValidator('RadiusSteil'), this.validatorService.rangeValidator(this.radiusSteilFeature)]);
            } else {
                this.form.get('radiusSteil').setValidators([this.validatorService.requiredFieldValidator('RadiusSteil'), this.validatorService.rangeValidator(this.radiusSteilFeature, this.i18nService.replaceFromCommaToDot(this.form.get('radiusFlach').value))]);
            }
            this.setValueWithFormat('RadiusSteil', 'radiusSteil');
        }
    }

    private recalculateRadiusSteilDegree() {
        if(this.form.get('radiusFlachDegree').value !== null) {
            let radiusFlachDegree = this.i18nService.replaceFromCommaToDot(this.form.get('radiusFlachDegree').value);

            let radiusSteilDegree = radiusFlachDegree + 90;
            if(radiusSteilDegree > 180) {
            radiusSteilDegree = radiusSteilDegree - 180;
            }
            this.form.patchValue({'radiusSteilDegree': this.i18nService.replaceFromDotToComma(radiusSteilDegree)});
        }
    }

    private recalculateTorizitalAstigmat() {
        if(this.form.get('radiusFlach').value !== null && this.form.get('radiusSteil').value !== null) {
            let radiusFlach = this.i18nService.replaceFromCommaToDot(this.form.get('radiusFlach').value);
            let radiusSteil = this.i18nService.replaceFromCommaToDot(this.form.get('radiusSteil').value);

            let torizitatCentral = radiusFlach - radiusSteil;
            if(torizitatCentral !== undefined && isFinite(torizitatCentral)) {
            torizitatCentral = +torizitatCentral.toFixed(2);
            this.form.patchValue({'torizitatCentral': this.i18nService.replaceFromDotToComma(torizitatCentral)});
            }
            let astigmat = (1.376 - 1.0)*(1/radiusFlach - 1/radiusSteil)*1000;
            if(astigmat !== undefined && isFinite(astigmat)) {
            astigmat = +astigmat.toFixed(2);
            this.form.patchValue({'astigmat': this.i18nService.replaceFromDotToComma(astigmat)});
            }
        }
    }

    checkRequiredFields() {

        if(this.form.get('radiusFlach').value === "") {
          this.form.get('radiusFlach').markAsTouched();
          this.form.get('radiusFlach').setValue("");
        }

        if(this.form.get('radiusFlachDegree').value === "") {
          this.form.get('radiusFlachDegree').markAsTouched();
          this.form.get('radiusFlachDegree').setValue("");
        }

        if(this.form.get('radiusSteil').value === "") {
          this.form.get('radiusSteil').markAsTouched();
          this.form.get('radiusSteil').setValue("");
        }

        if(this.form.get('eccentricity').value === "") {
          this.form.get('eccentricity').markAsTouched();
          this.form.get('eccentricity').setValue("");
        }

        if(this.form.get('hhDia').value === "") {
          this.form.get('hhDia').markAsTouched();
          this.form.get('hhDia').setValue("");
        }

        if(this.form.get('kCStadium').value === "") {
            this.form.get('kCStadium').markAsTouched();
            this.form.get('kCStadium').setValue("");
        }

        if(this.form.get('lensType').value === null) {
            this.form.get('lensType').markAsTouched();
            this.form.get('lensType').setValue("");
        }
      }

    public openEccentricityDialog() {
        const dialogRef = this.dialog.open( EccentricityDialogComponent, {
            width: '930px',
            data: {
                radiusFlach: (this.form.get('radiusFlach').value && this.form.get('radiusFlach').value !== '') ? this.i18nService.replaceFromCommaToDot(this.form.get('radiusFlach').value) : 0,
                radiusFlachDegree: (this.form.get('radiusFlachDegree').value && this.form.get('radiusFlachDegree').value !== '') ? this.i18nService.replaceFromCommaToDot(this.form.get('radiusFlachDegree').value) : 0,
                radiusSteil: (this.form.get('radiusSteil').value && this.form.get('radiusSteil').value !== '') ? this.i18nService.replaceFromCommaToDot(this.form.get('radiusSteil').value) : 0,
                radiusSteilDegree: (this.form.get('radiusSteilDegree').value && this.form.get('radiusSteilDegree').value !== '') ? this.i18nService.replaceFromCommaToDot(this.form.get('radiusSteilDegree').value) : 0,
                previousEccentricityDialogResult: this.previousEccentricityDialogResult,
                side: this.side
            },
            scrollStrategy: new NoopScrollStrategy()
        });

        dialogRef.afterClosed().subscribe(result => {
            if(result !== undefined) {
                this.previousEccentricityDialogResult = result;
                this.form.patchValue({'eccentricity': this.i18nService.replaceFromDotToComma(result.finalResult)})
            }
        });
    }

    getLimitsFormatted(rangeMin: number, rangeMax: number) {
        return this.i18nService.getLimitsFormatted(rangeMin, rangeMax);
    }

    setValueWithFormat(featureName: string, control: string) {
        this.form.controls[control].setValue(
            this.i18nService.replaceFromDotToComma(
                this.i18nService.formatIfNumber(this.form.controls[control].value, this.globalStorageService.decimalFormats.find(feature => feature.featureName === featureName)?.decimalFormat)
            )
        );
    }

    clearAll() {
        this.form.reset();

        this.form.get('kCStadium').setValue(0);
    }
}
